import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";

import { AgGridCustom as AgGrid, CustomSpin as Spin } from "_components";
import { GridContext, useInvoices } from "_contexts";
import { isNonEmptyArray, shallowCopy, deepCopy } from "_helpers";
import { useFolders } from "_hooks";

import { RechartsCustom as Recharts } from "./_Recharts";
import { gridOptions, columnDefs, createPinData } from "./_tableConfigs";
import { FolderDetail } from "./FolderDetail";

import { Col, Button, Divider, Row, Tabs, Tag } from "antd";
import { Collapse } from "bootstrap";
import dayjs from "dayjs";

import { ReactComponent as Arrow } from "_assets/left-arrow-white.svg";
import { ReactComponent as Xlsx } from "_assets/xlsx-icon.svg";

import styles from "_styles/Folders.module.css";

export function Folders() {
    const statusFilter = useRef("all");
    const stateFilter = useRef("all");
    const gridRef = useRef();

    const [ gridData, setGridData ] = useState([]);
    const [ chartsData, setChartsData ] = useState({});
    const [ tabsActiveKey, setTabsActiveKey ] = useState('dossiers');
    const [ closedFoldersIsEnable, setClosedFoldersIsEnable ] = useState(false);
    const [ gridDataAreUpdated, setGridDataAreUpdated ] = useState(false);
    const [ statusFilterStatus, setStatusFilterStatus ] = useState("all");
    const { fetchGridData, fetchChartsData, fetchChartsLegend, fetchDataAreReady, fetchIsDone } = useFolders();
    const { litigMessages, setLitigMessages, setCommunicationsState } = useInvoices();

    useEffect(() => { document.title = 'Espace client - Dossiers' }, []);

    useEffect(() => {
        if( fetchDataAreReady ){
            let gridDataCopy = deepCopy(fetchGridData), 
                chartsDataCopy = deepCopy(fetchChartsData);

            if( !closedFoldersIsEnable ){
                // On vide les valeurs des charts
                Object.values(chartsDataCopy).forEach(chart => {
                    chart.forEach(val => {
                        val.value = 0;
                    })
                })

                // On filtre sur ce qui nous intéresse
                gridDataCopy = gridDataCopy.filter(data => {
                    const { _status_closedEnd, _status_closedVoid } = data;
                    return !_status_closedEnd && !_status_closedVoid && !( _status_closedEnd && _status_closedVoid );
                })

                // On regénère les valeurs
                gridDataCopy.forEach(data => {
                    const { _status_id, inv_sumRest } = data;

                    Object.entries(chartsDataCopy).forEach(([key, chart]) => {
                        chart.forEach(val => {
                            const { id } = val;

                            if( id === _status_id ){
                                if( key === 'chart01' ) val.value += inv_sumRest;
                                if( key === 'chart02' ) val.value += 1;

                            }
                        })
                    })
                })
                
            }
            setGridData(gridDataCopy);
            setChartsData(chartsDataCopy);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closedFoldersIsEnable, fetchDataAreReady])

    useEffect(() => {
        if( isNonEmptyArray(litigMessages) ){
            let dataCopy = shallowCopy(gridData);
            const lastMessage = litigMessages[litigMessages.length-1];
            const { _file_id, msg_direction } = lastMessage;

            const index = dataCopy.findIndex(el => el._file_id === _file_id); 

            if( index !== -1 ){
                const { _status_closedEnd, _status_closedVoid, communication_state } = dataCopy[index];
                if( !_status_closedEnd && !_status_closedVoid ){
                    const translate = {
                        in: 'to_treat',
                        out: 'pending'
                    }

                    if( translate[msg_direction] !== communication_state ){
                        dataCopy[index]['communication_state'] = translate[msg_direction];
                        setGridData(dataCopy);
                        setGridDataAreUpdated(true);
                    }
                }
            }
            setLitigMessages([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [litigMessages])

    useEffect(() => {
        redefineCommunicationsState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridData])

    const isExternalFilterPresent = useCallback(( ) => statusFilter.current !== "all" || stateFilter.current !== "all", [statusFilter.current, stateFilter.current])
    const doesExternalFilterPass = useCallback(({ data }) => {
        const { _status_id, communication_state } = data;

        const statusFilterRef = statusFilter.current;
        const stateFilterRef = stateFilter.current;
        
        if( statusFilterRef !== 'all' && stateFilterRef !== 'all' ){
            return _status_id === statusFilterRef &&
                communication_state === stateFilterRef;
        }
        if( statusFilterRef !== 'all' ){
            return _status_id === statusFilterRef;
        }
        if( stateFilterRef !== 'all' ){
            return communication_state === stateFilterRef;
        }
        
        return true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter.current, stateFilter.current])

    const handleRowDataIsUpdate = useCallback(({ newData }) => {
        // console.log(newData)
    }, [])

    const handleChartSectorClick = useCallback(({ statusId }) => {
        const newVal = ( statusFilter.current !== statusId ) ? statusId : "all";
        statusFilter.current = newVal;
        setStatusFilterStatus(newVal);
        gridRef.current.api.onFilterChanged();
    }, [])

    const [ tabsItems, setTabsItems ] = useState([]);
    const handleRowDoubleClick = useCallback(({ accId }) => {
        setTabsItems((prevVal) => {
            const exists = prevVal.some(tab => tab.key === accId);
        
            if( exists ) {
              setTabsActiveKey(accId);
              return prevVal;
            }
        
            const newTab = {
                key: accId,
                label: accId,
                children: <FolderDetail acc_id={accId} />
            };
        
            setTabsActiveKey(accId);
            return [...prevVal, newTab];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const redefineCommunicationsState = useCallback(() => {
        if( isNonEmptyArray(gridData) ){
            const reduced = gridData.reduce((acc, val) => {
                const { communication_state, acc_id, _file_id } = val;

                if( communication_state ){
                    if( !acc[acc_id] ){
                        acc[acc_id] = [];
                    }
                    acc[acc_id].push({
                        file_id: _file_id,
                        communication_state
                    });
                }

                return acc;
            }, {});

            setCommunicationsState(reduced);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridData])

    const foldersData = {
        'fetchGridData': gridData,
        'fetchChartsData': chartsData,
        'fetchChartsLegend': fetchChartsLegend
    };

    useEffect(() => {
        if( gridDataAreUpdated ){
            const tabsItemsCopy = shallowCopy(tabsItems);
            const index = tabsItemsCopy.findIndex(t => t.key === tabsActiveKey);
    
            if( index !== -1 ){
                setTabsActiveKey(tabsItemsCopy[index]['key']);
            }
        }
        setGridDataAreUpdated(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridDataAreUpdated])

    const handleTabsChange = ( newKey ) => {
        setTabsActiveKey(newKey);
    }

    const handleRemoveTab = ( targetKey ) => {
        setTabsItems((prevVal) => {
            const filteredTabs = prevVal.filter(tab => tab.key !== targetKey);
        
            if( targetKey === tabsActiveKey ) {
                if( filteredTabs.length > 0 ) {
                    const lastTab = filteredTabs[filteredTabs.length - 1];
                    setTabsActiveKey(lastTab.key);
                } else {
                    setTabsActiveKey('dossiers');
                }
            }
        
            return filteredTabs;
        });
    };

    return(
        <div className="mt-1" style={{height: 'calc(100% - 32px)'}}> 
            {
                fetchDataAreReady && 
                fetchIsDone &&
                isNonEmptyArray(gridData) &&
                isNonEmptyArray(chartsData?.chart01) &&
                isNonEmptyArray(chartsData?.chart02) &&  
                isNonEmptyArray(fetchChartsLegend)
                ?
                    <Tabs 
                        activeKey={tabsActiveKey}
                        items={[{
                            key: 'dossiers',
                            label: 'Dossiers',
                            children: (
                                <FoldersComponent 
                                    gridRef={gridRef} 
                                    foldersData={foldersData} 
                                    stateFilter={stateFilter} 
                                    statusFilterStatus={statusFilterStatus} 
                                    closedFoldersIsEnable={closedFoldersIsEnable} 
                                    setClosedFoldersIsEnable={setClosedFoldersIsEnable}
                                    handleRowDataIsUpdate={handleRowDataIsUpdate} 
                                    handleChartSectorClick={handleChartSectorClick} 
                                    handleRowDoubleClick={handleRowDoubleClick}
                                    isExternalFilterPresent={isExternalFilterPresent}
                                    doesExternalFilterPass={doesExternalFilterPass}
                                />
                            ),
                            closable: false
                        },
                        ...tabsItems]} 
                        type="editable-card" 
                        className={["h-100", styles.tabsHeight].join(" ")}
                        hideAdd
                        onChange={handleTabsChange} 
                        onEdit={handleRemoveTab}
                    />
                :
                !fetchDataAreReady && fetchIsDone
                ?
                    <div className="d-flex justify-content-center align-items-center h-100">Aucune donnée à afficher</div>
                :
                    <Spin className="h-100" />
            }
        </div>
    );
}

function FoldersComponent({ 
    gridRef, 
    foldersData, 
    stateFilter,
    statusFilterStatus,
    closedFoldersIsEnable,
    setClosedFoldersIsEnable,
    handleChartSectorClick, 
    handleRowDataIsUpdate, 
    handleRowDoubleClick,
    isExternalFilterPresent,
    doesExternalFilterPass 
}){
    const [ panelIsOpen, setPanelIsOpen ] = useState(true);
    const [ stateFilterStatus, setStateFilterStatus ] = useState("all");

    const { fetchGridData, fetchChartsData, fetchChartsLegend } = foldersData;

    const myCollapse = document.getElementById('collapseTarget');

    useEffect(() => {
        if( myCollapse ){
            const bsCollapse = new Collapse(myCollapse, {toggle: false})
            panelIsOpen ? bsCollapse.show() : bsCollapse.hide()
        }
    }, [myCollapse, panelIsOpen])

    const handleFilterOnState = useCallback(( stateId ) => {
        const newVal = ( stateFilter.current !== stateId ) ? stateId : "all";
        stateFilter.current = newVal;
        setStateFilterStatus(newVal);
        gridRef.current.api.onFilterChanged();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const today = useMemo(() => ( dayjs().format('YYYYMMDD_HHmmss') ), [])

    const xlsxParams = useCallback(() => {
        return {
            fileName: 'Dossiers - ' + today,
            sheetName: 'Espace client - Dossiers',
            processCellCallback: (params) => {
                const colDef = params.column.getColDef();
                // try to reuse valueFormatter from the colDef
                if( colDef.valueFormatter && colDef.field == 'communication_state' ) {
                    const valueFormatterParams = {
                        ...params,
                        data: params.node.data,
                        node: params.node,
                        colDef: params.column.getColDef()
                    };
                    return colDef.valueFormatter(valueFormatterParams);
                }
                return params.value;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onBtnExport = useCallback(() => {
        gridRef.current.api.exportDataAsExcel(xlsxParams());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const iconsSize = useMemo(() => ({ height: 18, width: 18 }), []);
    const { height, width } = iconsSize;

    const statusMap = useRef({
        'S0_PRE': 'Inactif',
        'S1_OPEN': 'En-cours',
        'S2L_LITIG': 'Action externe',
        'S2J_JUDIC': 'Judiciaire',
        'S2P_PAY': 'Paiement',
        'SX_CLOSE': 'Clôture'
    }).current;
    const stateMap = useRef({
        'to_treat': 'A traiter par vous'
    }).current;

    return(
        <div className="h-100">
            <Row className="mb-2">
                <div className="d-flex align-items-center w-100">
                    {
                        ( statusFilterStatus !== 'all' || stateFilterStatus !== 'all' || closedFoldersIsEnable )
                        &&
                        <>
                            <span className="red-txt bld-txt">Filtre(s) actif(s)</span>&nbsp;:&nbsp;
                            {
                                [
                                    ...(statusFilterStatus !== 'all' ? [`Statut ${statusMap[statusFilterStatus]}`] : []),
                                    ...(stateFilterStatus !== 'all' ? [`État ${stateMap[stateFilterStatus]}`] : []),
                                    ...(closedFoldersIsEnable ? ['Dossiers fermés'] : [])
                                ].map((label, index, array) => (
                                    <span key={label}>
                                        <Tag bordered={false} className="pe-0, me-0">{label}</Tag>{ index < array.length - 1 && ",\u00A0" }
                                    </span>
                                ))
                            }
                        </>
                    }
                    <div className="d-flex ms-auto" style={{ gap: '8px' }}>
                        <Button
                            icon={<Xlsx height={height} width={width}/>}
                            styles={{
                                icon: { marginRight: '2px' }
                            }}
                            type="text"
                            onClick={() => onBtnExport()}
                        >
                            Exporter
                        </Button>
                        <Button type={stateFilterStatus !== 'all' ? 'primary' : undefined} onClick={() => handleFilterOnState('to_treat')}>
                            A traiter par vous
                        </Button>
                        <Button type={closedFoldersIsEnable ? 'primary' : undefined} onClick={() => setClosedFoldersIsEnable(prevVal => !prevVal)}>
                            Dossiers fermés
                        </Button>
                    </div> 
                </div>
            </Row>
            <Row gutter={16} style={{height: 'calc(100% - 34px)'}}>
                <Col className="h-100 collapse collapse-horizontal" id="collapseTarget" flex={panelIsOpen ? '550px' : 'none'}>
                    <Recharts 
                        fetchData={fetchChartsData} 
                        fetchLegend={fetchChartsLegend}
                        panelIsOpen={panelIsOpen}
                        callBacks={{
                            onChartSectorClick: handleChartSectorClick
                        }}
                    />
                </Col>
                <Col flex="50px" className="d-flex justify-content-center h-100">
                    <Row className="flex-column align-items-center">
                        <Divider type="vertical" className="mb-2" style={{height: "15px"}}/>
                            <Button 
                                type="primary"
                                className={styles.collapseButton}
                                shape="circle"
                                icon={<Arrow className={styles.svgIcon} height={height} width={width} viewBox="-3 0 20 20" transform={!panelIsOpen ? "rotate(180)" : undefined}/>}
                                onClick={() => setPanelIsOpen(!panelIsOpen)} 
                            />
                        <Divider type="vertical" className="mt-2" style={{flex: 1}}/>
                    </Row>
                </Col>
                <Col flex="auto" className="h-100">
                    <GridContext.Provider value={{ gridOptions, isExternalFilterPresent, doesExternalFilterPass }}>
                        <AgGrid
                            gridRef={gridRef} 
                            columnDefs={columnDefs}
                            createPinData={createPinData}
                            fetchData={fetchGridData}
                            callBacks={{
                                onRowDataIsUpdate: handleRowDataIsUpdate,
                                onRowDoubleClick: handleRowDoubleClick
                            }}
                        />
                    </GridContext.Provider>
                </Col>
            </Row>
        </div>
    );
}